import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createAmbulance,
  deleteAmbulance,
  fetchAmbulanceList,
  fetchDetailAmbulance,
  updateAmbulance,
} from '@/domains/asset-management/apis/ambulance-management';
import type {
  AmbulanceDetailResponse,
  AmbulanceID,
  AmbulanceListResponse,
  CreateAmbulancePayload,
  CreateAmbulanceResponse,
  DeleteAmbulanceResponse,
  UpdateAmbulanceParams,
  UpdateAmbulancePayload,
  UpdateAmbulanceResponse,
  UseAmbulanceManagementProps,
} from '@/domains/asset-management/types/ambulance';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const useAmbulanceManagement = ({
  ambulanceId = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  refetchInterval,
}: UseAmbulanceManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: ambulanceListData, isLoading: isLoadingAmbulanceList } =
    useQuery<AmbulanceListResponse>({
      queryKey: ['ambulanceListData', queryParams],
      queryFn: () => fetchAmbulanceList(queryParams),
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
      refetchInterval: refetchInterval,
      refetchIntervalInBackground: true,
      initialData: {
        code: '-',
        message: '-',
        data: { total: 0, page: 1, size: 10, total_pages: 0, rows: [] },
      },
      initialDataUpdatedAt: 0,
    });

  const { data: detailAmbulanceData, refetch: refetchDetailAmbulance } = useQuery<
    AmbulanceDetailResponse,
    Error
  >({
    queryKey: ['detailAmbulanceData', ambulanceId],
    queryFn: () => fetchDetailAmbulance(ambulanceId as AmbulanceID),
    retry: false,
    enabled: !!ambulanceId,
  });

  const createAmbulanceMutation = useMutation<
    CreateAmbulanceResponse,
    Error,
    CreateAmbulancePayload
  >({
    mutationFn: (data) => createAmbulance(data),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['ambulanceListData', queryParams] });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteAmbulanceMutation = useMutation<DeleteAmbulanceResponse, Error, AmbulanceID>({
    mutationFn: (ambulanceId) => deleteAmbulance(ambulanceId),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({ queryKey: ['ambulanceListData', queryParams] });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateAmbulanceMutation = useMutation<
    UpdateAmbulanceResponse,
    Error,
    { ambulanceData: UpdateAmbulancePayload; params: UpdateAmbulanceParams }
  >({
    mutationFn: ({ ambulanceData, params }) => updateAmbulance(ambulanceData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['ambulanceListData', queryParams] });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    ambulanceListData,
    isLoadingAmbulanceList,
    detailAmbulanceData,
    refetchDetailAmbulance,
    createAmbulance: createAmbulanceMutation.mutate,
    updateAmbulance: updateAmbulanceMutation.mutate,
    deleteAmbulance: deleteAmbulanceMutation.mutate,
  };
};

export default useAmbulanceManagement;
